/**
 * TrustCategoryPage Component
 * Displays detailed information, upcoming events, subgroup lists, and booking options
 * for specific trust categories ('community', 'sports', 'activities') via dynamic route parameters (`/trust/:category`).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import TicketModal from '../../Components/Trust/TicketModal';
import { fetchJsonWithBackendFallback } from '../../utils/backendApi';
import { getSiteContent } from '../../utils/siteContent';
import './TrustCategoryPage.css';

// Content configuration map for supported trust category routes
const CATEGORY_DETAILS = {
  community: {
    title: 'Our Sub-Groups',
    intro: 'These sub-groups represent the trust’s own clubs, teams and friendship groups designed to support members and keep our institute lively and welcoming.',
    roleTitle: 'What this section does',
    roleText: 'Our Sub-Groups are the trust-owned social club, friendship circles, cricket team, junior and adult football teams, and bowls group that meet here to build community spirit.',
    highlights: [
      'Trust-run social clubs and friendship groups',
      'Cricket, football, bowls and team activities',
      'Regular gatherings that connect members and neighbours',
    ],
  },
  sports: {
    title: 'Sports Activities',
    intro: 'The sports offer gives people of all ages a chance to stay active, enjoy friendly competition, and take part in healthy routines together.',
    roleTitle: 'Why it matters',
    roleText: 'Sport at Clipstone brings people together through teamwork, wellbeing, and opportunities to try something new.',
    highlights: [
      'Friendly matches and team sessions',
      'Activities for different ages and abilities',
      'A welcoming place to build confidence and fitness',
    ],
  },
  activities: {
    title: 'Community Groups and Activities',
    intro: 'These pages highlight community groups that are not owned by the trust but hold their events and sessions at our institute, creating a shared local hub.',
    roleTitle: 'How it supports residents',
    roleText: 'Community Groups and Activities showcase external organisations and groups that use our facilities for gatherings, workshops, and community-led events.',
    highlights: [
      'Events run by external community groups at our institute',
      'Organisations bringing diverse local activities to Clipstone',
      'Shared spaces for connection, learning and support',
    ],
  },
};

function TrustCategoryPage() {
  const { category } = useParams();
  const categoryKey = (category || 'community').toLowerCase();
  const details = useMemo(() => CATEGORY_DETAILS[categoryKey] || CATEGORY_DETAILS.community, [categoryKey]);
  const [siteContent, setSiteContent] = useState(() => getSiteContent());

  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = useMemo(() => [
    { id: 1, text: `${details.title} Overview`, color: '#1e3f32' },
    { id: 2, text: 'Join our upcoming sessions', color: '#2d6a4f' },
    { id: 3, text: 'Engage with the community', color: '#a67c52' },
  ], [details.title]);
  const [selectedEventForBooking, setSelectedEventForBooking] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    const handleContentUpdate = () => setSiteContent(getSiteContent());
    window.addEventListener('site-content-updated', handleContentUpdate);
    return () => window.removeEventListener('site-content-updated', handleContentUpdate);
  }, []);

  const pageContent = useMemo(() => {
    if (categoryKey === 'sports') {
      return {
        intro: siteContent.trust.sportsIntro,
        roleTitle: siteContent.trust.sportsRoleTitle,
        roleText: siteContent.trust.sportsRoleText,
      };
    }

    if (categoryKey === 'activities') {
      return {
        intro: siteContent.trust.activitiesIntro,
        roleTitle: siteContent.trust.activitiesRoleTitle,
        roleText: siteContent.trust.activitiesRoleText,
      };
    }

    return {
      intro: siteContent.trust.communityIntro,
      roleTitle: siteContent.trust.communityRoleTitle,
      roleText: siteContent.trust.communityRoleText,
    };
  }, [categoryKey, siteContent]);

  const [upcomingEvent, setUpcomingEvent] = useState(null);
  const [categoryContent, setCategoryContent] = useState([]);
  const [trustees, setTrustees] = useState([]);
  const [contentLoading, setContentLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showManageEventsLink, setShowManageEventsLink] = useState(false);
  const [eventLinkMap, setEventLinkMap] = useState({});
  const [hoveredContentItemId, setHoveredContentItemId] = useState(null);
  const [selectedContentItemId, setSelectedContentItemId] = useState(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [modalSlidePaused, setModalSlidePaused] = useState(false);
  const [modalShowFullImage, setModalShowFullImage] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    const normalizeImageList = (value) => {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (typeof value === 'string' && value.trim()) return [value.trim()];
      return [];
    };

    const getStoredCategoryImageMap = () => {
      if (typeof window === 'undefined') return {};
      try {
        const stored = window.localStorage.getItem('clipstone_category_content_images');
        return stored ? JSON.parse(stored) : {};
      } catch (err) {
        console.error('Unable to load stored category image map:', err);
        return {};
      }
    };

    const getStoredCategoryLinkMap = () => {
      if (typeof window === 'undefined') return {};
      try {
        const stored = window.localStorage.getItem('clipstone_category_content_links');
        return stored ? JSON.parse(stored) : {};
      } catch (err) {
        console.error('Unable to load stored category link map:', err);
        return {};
      }
    };

    const getStoredEventLinkMap = () => {
      if (typeof window === 'undefined') return {};
      try {
        const stored = window.localStorage.getItem('clipstone_event_links');
        return stored ? JSON.parse(stored) : {};
      } catch (err) {
        console.error('Unable to load stored event link map:', err);
        return {};
      }
    };

    const checkAdminAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user?.email) {
          if (isMounted) setShowManageEventsLink(false);
          return;
        }

        const { data: isAdmin } = await supabase.rpc('check_is_admin', {
          p_email: session.user.email,
        });

        if (isMounted) setShowManageEventsLink(Boolean(isAdmin));
      } catch (err) {
        console.error('Trust category admin access check error:', err);
        if (isMounted) setShowManageEventsLink(false);
      }
    };

    const fetchUpcomingEvent = async () => {
      setLoading(true);
      try {
        const storedEventLinks = getStoredEventLinkMap();
        if (isMounted) setEventLinkMap(storedEventLinks);

        const { data, error } = await supabase
          .from('tbl_events')
          .select('*')
          .eq('category', categoryKey)
          .order('event_date', { ascending: true })
          .limit(1);

        if (!isMounted) return;

        if (error) {
          console.error('Could not load trust category events:', error);
          setUpcomingEvent(null);
        } else {
          setUpcomingEvent((data && data[0]) || null);
        }
      } catch (err) {
        console.error('Trust category event fetch error:', err);
        if (isMounted) setUpcomingEvent(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const fetchCategoryContent = async () => {
      setContentLoading(true);
      try {
        const data = await fetchJsonWithBackendFallback('/bookings/category-content');
        const nextItems = Array.isArray(data)
          ? data.filter((item) => !item.category || item.category === categoryKey)
          : [];
        const storedImageMap = getStoredCategoryImageMap();
        const storedLinkMap = getStoredCategoryLinkMap();
        const normalizedItems = nextItems.map((item) => {
          const imageUrls = normalizeImageList(storedImageMap[item.id] || item.image_urls || item.image_url);
          return {
            ...item,
            image_urls: imageUrls,
            image_url: imageUrls[0] || null,
            website_url: item.website_url || storedLinkMap[item.id] || '',
          };
        });
        if (isMounted) {
          setCategoryContent(normalizedItems);
          window.localStorage.setItem('clipstone_category_content', JSON.stringify(normalizedItems));
        }
      } catch (err) {
        console.error('Trust category content fetch error:', err);
        if (typeof window !== 'undefined') {
          const storedContent = window.localStorage.getItem('clipstone_category_content');
          if (storedContent) {
            try {
              const parsedContent = JSON.parse(storedContent);
              const nextItems = Array.isArray(parsedContent)
                ? parsedContent.filter((item) => !item.category || item.category === categoryKey)
                : [];
              const storedLinkMap = getStoredCategoryLinkMap();
              const normalizedItems = nextItems.map((item) => {
                const imageUrls = normalizeImageList(item.image_urls || item.image_url);
                return {
                  ...item,
                  image_urls: imageUrls,
                  image_url: imageUrls[0] || null,
                  website_url: item.website_url || storedLinkMap[item.id] || '',
                };
              });
              if (isMounted) setCategoryContent(normalizedItems);
            } catch (parseErr) {
              console.error('Unable to parse stored category content:', parseErr);
              if (isMounted) setCategoryContent([]);
            }
          } else if (isMounted) {
            setCategoryContent([]);
          }
        } else if (isMounted) {
          setCategoryContent([]);
        }
      } finally {
        if (isMounted) setContentLoading(false);
      }
    };

    checkAdminAccess();
    fetchUpcomingEvent();
    fetchCategoryContent();

    return () => {
      isMounted = false;
    };
  }, [categoryKey]);

  useEffect(() => {
    const storedTrustees = window.localStorage.getItem('clipstone_trustees');
    if (storedTrustees) {
      try {
        const parsedTrustees = JSON.parse(storedTrustees);
        if (Array.isArray(parsedTrustees)) {
          setTrustees(parsedTrustees);
        }
      } catch (err) {
        console.error('Unable to load trustee lookup data:', err);
      }
    }
  }, []);

  const selectedTrusteeId = new URLSearchParams(location.search).get('trustee');

  const formatDate = (value) => {
    if (!value) return 'Date to be confirmed';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getEventWebsiteUrl = (eventItem) => {
    if (!eventItem) return '';
    return eventItem.event_url || eventItem.website_url || eventLinkMap[eventItem.id] || '';
  };

  const getActivityWebsiteUrl = (item) => {
    if (!item) return '';
    return item.website_url || '';
  };

  const getAssociatedTrusteeInfo = (item) => {
    if (!item) return null;

    const fallbackName = item.trustee_name || item.trustee || item.assigned_trustee_name || item.associated_trustee_name || '';
    const matchingTrustee = trustees.find((trustee) => {
      const trusteeSlug = trustee.name?.toLowerCase().replace(/\s+/g, '-');
      return (
        trustee.id === item.trustee_id ||
        trustee.id === item.trustee ||
        trusteeSlug === item.trustee_id ||
        trusteeSlug === item.trustee ||
        trustee.name === fallbackName
      );
    });

    const trusteeId = item.trustee_id || item.trustee || null;
    const trusteeName = matchingTrustee?.name || fallbackName || 'Unknown trustee';

    return {
      id: trusteeId,
      name: trusteeName,
      hasTrustee: Boolean(trusteeId || fallbackName),
    };
  };

  const selectedContentItem = categoryContent.find((item) => item.id === selectedContentItemId) || null;

  const getItemImages = (item) => {
    if (!item) return [];
    if (Array.isArray(item.image_urls) && item.image_urls.length > 0) return item.image_urls.filter(Boolean);
    if (item.image_url) return [item.image_url];
    return [];
  };

  const selectedContentImages = getItemImages(selectedContentItem);
  const activeModalImage = selectedContentImages[modalImageIndex] || selectedContentImages[0] || null;

  useEffect(() => {
    setModalImageIndex(0);
    setModalSlidePaused(false);
    setModalShowFullImage(false);
  }, [selectedContentItemId]);

  useEffect(() => {
    if (!selectedContentItem || selectedContentImages.length <= 1 || modalSlidePaused) return undefined;

    const intervalId = window.setInterval(() => {
      setModalImageIndex((prev) => (prev + 1) % selectedContentImages.length);
    }, 2800);

    return () => window.clearInterval(intervalId);
  }, [selectedContentItem, selectedContentImages.length, modalSlidePaused]);

  return (
    <div className="trust-category-page">
      <div className="trust-category-hero">
        <p className="trust-category-kicker">Community Trust</p>
        <h1 id="trust-category-overview-heading">{details.title}</h1>
        <p>{pageContent.intro}</p>
        <div className="trust-category-actions">
          <Link to="/trust" className="trust-category-link"><i className="icon-left" style={{ marginRight: '6px' }} /> Back to Community Trust</Link>
          {showManageEventsLink && (
            <Link to="/admin/events" className="trust-category-link trust-category-link--admin">Manage events</Link>
          )}
        </div>
      </div>

      <div className="slider-container">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`slide ${index === currentSlide ? 'active' : ''}`}
            style={{ background: slide.color }}
          >
            <div className="slide-content">{slide.text}</div>
          </div>
        ))}
        <div className="slider-dots">
          {slides.map((_, index) => (
            <span key={index} className={`slider-dot ${index === currentSlide ? 'active' : ''}`} onClick={() => setCurrentSlide(index)} />
          ))}
        </div>
      </div>

      <div className="trust-category-grid">
        <article className="trust-category-card">
          <h2>{pageContent.roleTitle}</h2>
          <p>{pageContent.roleText}</p>
          <ul>
            {details.highlights.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>

        <article className="trust-category-card">
          <h2>What this section offers</h2>
          <p>Each area is designed to give residents a clear and welcoming entry point into the work of the trust, whether they are looking for a club, a team, or a new experience.</p>
          <p>Events and activities shown here are added through the admin control panel and update automatically for visitors.</p>
        </article>

        <article className="trust-category-card trust-category-card--event">
          <h2>Upcoming event</h2>
          <div id="trust-category-upcoming-event-panel">
          {loading ? (
            <div className="trust-category-event-box trust-category-event-box--loading">
              <p>Loading the next event…</p>
            </div>
          ) : upcomingEvent ? (
            <div className="trust-category-event-stack">
              <div className="trust-category-event-box trust-category-event-box--title">
                <h3>{upcomingEvent.title}</h3>
              </div>
              <div className="trust-category-event-box trust-category-event-box--details">
                {upcomingEvent.image_url && (
                  <div style={{ marginBottom: '0.8rem', display: 'inline-flex', width: '100%', maxWidth: '280px', height: '140px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #dce9df', background: '#f7fbf8' }}>
                    <img src={upcomingEvent.image_url} alt={upcomingEvent.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <p>{upcomingEvent.description || 'More details will be shared soon.'}</p>
                <div className="trust-category-event-meta">
                  <span>{formatDate(upcomingEvent.event_date)}</span>
                  {upcomingEvent.location && <span>{upcomingEvent.location}</span>}
                </div>
                {getEventWebsiteUrl(upcomingEvent) && (
                  <div style={{ marginTop: '0.55rem' }}>
                    <a href={getEventWebsiteUrl(upcomingEvent)} target="_blank" rel="noreferrer" style={{ color: '#0f766e', fontWeight: 700, textDecoration: 'none' }}>
                      Visit event website
                    </a>
                  </div>
                )}
                <div id="trust-category-book-event-target" style={{ marginTop: '0.85rem' }}>
                  <button type="button" className="booking-submit-btn" onClick={() => setSelectedEventForBooking(upcomingEvent)}>
                    Book a session for this event
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="trust-category-event-stack">
              <div className="trust-category-event-box trust-category-event-box--empty">
                <p>No upcoming event is currently listed for this section.</p>
              </div>
              <div id="trust-category-book-event-target" className="trust-category-event-box trust-category-event-box--empty">
                <p>Booking becomes available here as soon as the next event is published.</p>
              </div>
            </div>
          )}
          </div>

          <div className="trust-category-section-divider" />
          <h2 id="trust-category-activity-info-panel" style={{ marginTop: '1rem' }}>Activity information</h2>
          {contentLoading ? (
            <div className="trust-category-event-box trust-category-event-box--loading">
              <p>Loading activity details…</p>
            </div>
          ) : categoryContent.length > 0 ? (
            <div className="trust-category-event-stack">
              {categoryContent.map((item) => {
                const associatedTrustee = getAssociatedTrusteeInfo(item);
                const trusteeName = associatedTrustee?.name || 'Unknown trustee';
                const trusteeNoteVisible = associatedTrustee?.hasTrustee && (!selectedTrusteeId || associatedTrustee.id === selectedTrusteeId || String(associatedTrustee.id || '') === String(selectedTrusteeId));
                const activityWebsiteUrl = getActivityWebsiteUrl(item);

                return (
                  <div key={item.id} className="trust-category-event-box trust-category-event-box--info">
                    <div className="trust-category-event-title-row">
                      <h3>{item.title}</h3>
                      <span className="trust-category-status-pill">{item.status || 'Hosted'}</span>
                    </div>
                    {item.description && <p>{item.description}</p>}
                    {getItemImages(item).length > 0 && (
                      <div
                        className="trust-category-content-image-shell"
                        style={{
                          position: 'relative',
                          display: 'flex',
                          width: '180px',
                          height: '120px',
                          marginTop: '0.65rem',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: '1px solid #dce9df',
                          background: '#f7fbf8',
                          cursor: 'pointer',
                          boxShadow: '0 8px 18px rgba(30, 63, 50, 0.08)'
                        }}
                        onMouseEnter={() => setHoveredContentItemId(item.id)}
                        onMouseLeave={() => setHoveredContentItemId(null)}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedContentItemId(item.id);
                        }}
                      >
                        <img src={getItemImages(item)[0]} alt={item.title} className="trust-category-content-image" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.2s ease' }} />
                        {hoveredContentItemId === item.id && (
                          <div className="trust-category-image-preview-card" style={{
                            position: 'absolute',
                            top: '110%',
                            left: 0,
                            zIndex: 20,
                            width: 'min(320px, 90vw)',
                            background: '#ffffff',
                            border: '1px solid #dce9df',
                            borderRadius: '14px',
                            boxShadow: '0 16px 36px rgba(30, 63, 50, 0.16)',
                            overflow: 'hidden',
                            display: 'grid',
                            gridTemplateColumns: '120px 1fr'
                          }}>
                            <img src={getItemImages(item)[0]} alt={item.title} className="trust-category-image-preview-card-img" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <div className="trust-category-image-preview-card-body" style={{ padding: '0.8rem' }}>
                              <h4 style={{ margin: '0 0 0.35rem', color: '#1e3f32', fontSize: '0.95rem' }}>{item.title}</h4>
                              <p style={{ margin: '0 0 0.35rem', color: '#4b5563', fontSize: '0.9rem', lineHeight: 1.45 }}>{item.description || 'More details will be shared soon.'}</p>
                              <p className="trust-category-details" style={{ marginTop: '0.3rem' }}>{item.details || 'Click to open the full activity details.'}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {associatedTrustee?.hasTrustee && (
                      <div className="trust-category-trustee-chip" role="status" aria-label={`This activity is led by ${trusteeName}`}>
                        <span className="trust-category-trustee-chip-icon">👤</span>
                        <span className="trust-category-trustee-chip-text">
                          Led by <strong>{trusteeName}</strong>
                        </span>
                      </div>
                    )}
                    {trusteeNoteVisible && selectedTrusteeId && (
                      <div className="trust-category-trustee-chip-note">This activity is linked to the trustee you selected.</div>
                    )}
                    {item.details && <p className="trust-category-details">{item.details}</p>}
                    {activityWebsiteUrl && (
                      <a
                        href={activityWebsiteUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="trust-category-website-link"
                      >
                        Visit activity website
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="trust-category-event-box trust-category-event-box--empty">
              <p>No additional activity information is available right now.</p>
            </div>
          )}
        </article>
      </div>

      {selectedContentItem && (
        <div className="trust-category-image-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(17, 24, 39, 0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.25rem', zIndex: 1000 }} onClick={() => setSelectedContentItemId(null)}>
          <div className="trust-category-image-modal" style={{ position: 'relative', width: 'min(1080px, 100%)', maxHeight: '94vh', overflow: 'auto', background: '#ffffff', borderRadius: '20px', boxShadow: '0 28px 70px rgba(17, 24, 39, 0.35)' }} onClick={(event) => { event.stopPropagation(); if (modalSlidePaused) setModalSlidePaused(false); }}>
            <button type="button" className="trust-category-image-modal-close" style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', border: 'none', background: 'rgba(255, 255, 255, 0.9)', color: '#374151', width: '36px', height: '36px', borderRadius: '50%', fontSize: '1.2rem', cursor: 'pointer' }} onClick={() => setSelectedContentItemId(null)} aria-label="Close activity preview">
              ×
            </button>
            {selectedContentImages.length > 1 && (
              <div style={{ position: 'absolute', top: '0.9rem', left: '1rem', display: 'flex', gap: '8px', zIndex: 6, background: 'rgba(255,255,255,0.9)', borderRadius: '999px', padding: '6px 10px' }}>
                {selectedContentImages.map((_, index) => (
                  <button
                    key={`${selectedContentItem.id}-dot-${index}`}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setModalImageIndex(index);
                      setModalSlidePaused(true);
                    }}
                    aria-label={`Show image ${index + 1}`}
                    style={{
                      width: modalImageIndex === index ? '12px' : '10px',
                      height: modalImageIndex === index ? '12px' : '10px',
                      borderRadius: '50%',
                      border: 'none',
                      background: modalImageIndex === index ? '#1e3f32' : '#9ca3af',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  />
                ))}
              </div>
            )}
            {activeModalImage && (
              <img
                src={activeModalImage}
                alt={selectedContentItem.title}
                className="trust-category-image-modal-image"
                style={{
                  width: '100%',
                  maxHeight: modalShowFullImage ? '78vh' : '640px',
                  objectFit: modalShowFullImage ? 'contain' : 'cover',
                  display: 'block',
                  cursor: 'pointer',
                  background: modalShowFullImage ? '#111827' : 'transparent',
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  if (selectedContentImages.length > 1) {
                    setModalSlidePaused(true);
                  }
                  setModalShowFullImage((prev) => !prev);
                }}
              />
            )}
            {selectedContentImages.length > 1 && (
              <div style={{ padding: '0.7rem 1.1rem 0', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {selectedContentImages.map((image, index) => (
                  <img
                    key={`${selectedContentItem.id}-modal-image-${index}`}
                    src={image}
                    alt={`${selectedContentItem.title} ${index + 1}`}
                    style={{
                      width: '74px',
                      height: '52px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: modalImageIndex === index ? '2px solid #1e3f32' : '1px solid #d1d5db',
                      cursor: 'pointer',
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setModalImageIndex(index);
                      setModalSlidePaused(true);
                    }}
                  />
                ))}
              </div>
            )}
            <div className="trust-category-image-modal-body" style={{ padding: '1rem 1.1rem 1.2rem' }}>
              <h3 style={{ margin: '0 0 0.3rem', color: '#1e3f32' }}>{selectedContentItem.title}</h3>
              <p className="trust-category-image-modal-status" style={{ margin: '0 0 0.5rem', color: '#a67c52', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.8rem' }}>{selectedContentItem.status || 'Hosted'}</p>
              {selectedContentImages.length > 1 && (
                <p style={{ margin: '0 0 0.65rem', color: '#6b7280', fontSize: '0.85rem' }}>
                  {modalSlidePaused ? 'Slideshow paused. Click outside the image to resume.' : 'Slideshow is auto-playing. Click an image to pause.'}
                </p>
              )}
              <p style={{ margin: '0 0 0.65rem', color: '#6b7280', fontSize: '0.85rem' }}>
                Click the main image to {modalShowFullImage ? 'return to cropped view' : 'view the full image uncut'}.
              </p>
              <p style={{ color: '#4b5563', lineHeight: 1.6 }}>{selectedContentItem.description || 'More details will be shared soon.'}</p>
              {selectedContentItem.details && <p style={{ marginTop: '0.65rem', color: '#4b5563', lineHeight: 1.6 }}>{selectedContentItem.details}</p>}
              {getActivityWebsiteUrl(selectedContentItem) && (
                <a
                  href={getActivityWebsiteUrl(selectedContentItem)}
                  target="_blank"
                  rel="noreferrer"
                  className="trust-category-website-link"
                >
                  Visit activity website
                </a>
              )}
              {selectedContentItem && (() => {
                const associatedTrustee = getAssociatedTrusteeInfo(selectedContentItem);
                return associatedTrustee?.hasTrustee ? (
                  <p style={{ marginTop: '1rem', color: '#134e4a', fontWeight: 700 }}>
                    Associated with trustee: <span style={{ color: '#0f766e' }}>{associatedTrustee.name || 'Unknown trustee'}</span>
                  </p>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      )}

      {selectedEventForBooking && (
        <TicketModal
          event={selectedEventForBooking}
          onClose={() => setSelectedEventForBooking(null)}
        />
      )}
    </div>
  );
}

export default TrustCategoryPage;
